import {RefObject, useEffect} from "react";

export interface UseIntersectionOptions {
    /**
     * Threshold for intersection ratio (0.0 to 1.0)
     * @default 0.1
     */
    threshold?: number | Array<number>;

    /**
     * Root margin for the intersection observer
     * @default "0px"
     */
    rootMargin?: string;

    /**
     * Root element for intersection calculation
     * @default null (viewport)
     */
    root?: Element | null;
}

export interface UseIntersectionCallbacks {
    /**
     * Called when element becomes visible
     */
    onEnter?: (entry: IntersectionObserverEntry) => void;

    /**
     * Called when element becomes invisible
     */
    onLeave?: (entry: IntersectionObserverEntry) => void;

    /**
     * Called on every intersection change
     */
    onChange?: (entry: IntersectionObserverEntry, isVisible: boolean) => void;
}

/**
 * Hook for observing element intersection with viewport
 *
 * @example
 * ```tsx
 * const ref = useRef<HTMLElement>(null);
 * useIntersection(ref, {
 *   onEnter: (entry) => console.log('Entered viewport'),
 *   onLeave: (entry) => console.log('Left viewport'),
 * });
 * ```
 */
export function useIntersection(
    elementRef: RefObject<Element | null>,
    callbacks: UseIntersectionCallbacks,
    options: UseIntersectionOptions = {},
): void {
    const {threshold = 0.1, rootMargin = "0px", root = null} = options;
    const {onEnter, onLeave, onChange} = callbacks;

    useEffect(() => {
        const element = elementRef.current;
        if (!element) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry) {
                    return;
                }

                const isVisible = entry.isIntersecting && entry.intersectionRatio > 0;

                if (onChange) {
                    onChange(entry, isVisible);
                }

                if (isVisible) {
                    onEnter?.(entry);
                } else {
                    onLeave?.(entry);
                }
            },
            {
                threshold,
                rootMargin,
                root,
            },
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [elementRef, onEnter, onLeave, onChange, threshold, rootMargin, root]);
}
